import { formatDate } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { FormGroup, FormControl, Validators } from '@angular/forms';

import { Subscription } from 'rxjs';
import { NgxUiLoaderService } from 'ngx-ui-loader';

import { User } from 'src/app/core/models/user.model';
import { Holding } from 'src/app/core/models/holding.model';
import { Companie } from 'src/app/core/models/companie.model';
import { UserService } from 'src/app/core/services/user.service';
import { SwalService } from 'src/app/shared/services/swal.service';
import { UtilsService } from 'src/app/shared/services/utils.service';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { HoldingService } from 'src/app/core/services/holding.service';
import { CompanieService } from 'src/app/core/services/companie.service';
import Swal from 'sweetalert2';


@Component({
  selector: 'app-companie-detail',
  templateUrl: './companie-detail.component.html',
  styleUrls: ['./companie-detail.component.css']
})
export class CompanieDetailComponent implements OnInit, OnDestroy {
  actualId: string;
  situation = [true, false];
  submitMode = false;
  loaderConfig: any;
  companieForm: FormGroup;
  companieUsers: User[] = [];
  actualCompany: Companie;
  userDataSource: MatTableDataSource<any>;
  companieHolding: Holding[];
  displayedColumns = ['Name', 'apiToken', 'isActive', 'actions'];
  holdingDataSource: MatTableDataSource<any>;
  routeSubscription: Subscription;
  dataLimiteFormatada: any;
  displayedColumnHolding = ['Name'];

  constructor(
    private router: Router,
    private swalService: SwalService,
    private userService: UserService,
    private utilsService: UtilsService,
    private loaderService: LoaderService,
    private activatedRoute: ActivatedRoute,
    private holdingService: HoldingService,
    private companieService: CompanieService,
    private ngxLoaderService: NgxUiLoaderService,

  ) { }

  ngOnInit() {
    this.setCompanie();
    this.loaderConfig = this.loaderService.getDefaultLoaderConfig();
  }

  /**
   * Pega o id da URL e armazena no `this.actualId`
   */
  getActualId() {
    this.routeSubscription = this.activatedRoute.params.subscribe((params: Params) => {
      this.actualId = params.id;
    });
  }

  /**
   * Seta `this.actualCompany` com a Empresa armazenada no `history.state`.
   * Caso não existe essa informação ela busca da API
   * Depois traz os Usuários e Grupos atrelados com a Empresa, constrói o
   * formulário e seta os dados das tabelas
   */
  async setCompanie() {
    this.ngxLoaderService.start();
    this.getActualId();

    this.actualCompany = await this.utilsService.setCompanie(this.actualId);

    try {
      this.dataLimiteFormatada = formatDate(this.actualCompany.licenceDate, 'yyyy-MM-dd', 'pt', '+0000');

    } catch {

      const year  = this.actualCompany.licenceDate.substr(6, 4);
      const month = this.actualCompany.licenceDate.substr(3, 2);
      const day   = this.actualCompany.licenceDate.substr(0, 2);

      const date = new Date(`${year}-${month}-${day}`)

      this.dataLimiteFormatada = formatDate(date, 'yyyy-MM-dd', 'en', '+0000');
    }

    await this.mountPageData();
    this.ngxLoaderService.stop();
  }

  dateFormat(value: string, justDate?: boolean): string {
    let string = value.replace(/-/g, '/').replace(/T/g, ' ').substr(0, 16);

    if (justDate) {
      string = value.replace(/-/g, '/').replace(/T/g, ' ').substr(0, 10);
    }

    return formatDate(string, 'dd/MM/yyyy', 'en', '+0000');
  }
  /**
   * Traz os dados necessários para a montagem da página
   * e Monta as tabelas e o formulário
   */
  async mountPageData() {
    await this.getUsers();
    await this.getHolding();
    this.buidCompanieForm();
    this.setDataTable(this.companieUsers, this.companieHolding);
  }

  /**
   * Traz os Usuários atrelados com a Empresa Atual
   */
  async getUsers() {
    this.getActualId();
    const response = await this.userService.getAllUsersByCompanie(this.actualId);
    this.companieUsers = response;
  }

  /**
   * Traz os Grupos atrelados com a Empresa Atual
   */
  async getHolding() {
    const response = await this.holdingService.getAllHoldingByCompanie(this.actualId);
    this.companieHolding = response;
  }

  /**
   * Preenche as tabelas do Usuário e Grupos com os dados vindo da API
   *
   * @param {Object[]} userDataTable
   * @param {Object[]} holdingDataTable
   */
  setDataTable(userDataTable: User[], holdingDataTable: Holding[]) {
    this.userDataSource = new MatTableDataSource(userDataTable);
    this.holdingDataSource = new MatTableDataSource(holdingDataTable);
  }

  /**
   * Construção do formulário da Empresa
   */
  buidCompanieForm() {
    this.companieForm = new FormGroup({
      id: new FormControl(this.actualCompany.id),
      name: new FormControl(this.actualCompany.name, [Validators.required]),
      email: new FormControl(this.actualCompany.email),
      admin: new FormControl(this.actualCompany.admin),
      apiToken: new FormControl(this.actualCompany.apiToken),
      expirationDate: new FormControl(this.actualCompany.licenceDate),
      isActive: new FormControl(this.actualCompany.isActive, [Validators.required]),
      code: new FormControl(this.actualCompany.code),
    });
  }

  async regenerateApiToken() {
    const confirmModal = await this.swalService.fireConfirmSwal();

    if (confirmModal.value) {
      const response = await this.companieService.regenerateApiToken(this.actualId);
      this.companieForm.controls.apiToken.setValue(response.apiToken);
    }

    return true;
  }

  async onSubmit() {
    this.submitMode = true;

    if (this.companieForm.invalid) {
      return this.swalService.fireErrorSwal('É necessário preencher os campos obrigátorios!');
    }

    this.getActualId();
    const objectCompany = this.companieForm.value;

    const confirmModal = await this.swalService.fireConfirmSwal();

    if (confirmModal.value) {
      const responseCompany = await this.companieService.updateCompany(objectCompany, this.actualId);

      if (responseCompany) {
        this.swalService.fireSuccessSwal(responseCompany.message);
      }

    }

    this.router.navigate(['/company']);

    this.submitMode = false;
  }


  changeAdmin(userId, userName){
    Swal.fire({
      type: 'warning',
      confirmButtonText: 'Sim, alterar administrador',
      showCancelButton: true,
      cancelButtonText: 'Não, cancelar',
      title: 'Atenção',
      html: `
        <p>Tem certeza de que deseja tornar o usuário <b>${userName}</b> administrador?</p>
        <p><b>OBS:</b> O administrador anterior será substituído</p>
      `
    }).then(result => {
      if (result.value) {
        this.ngxLoaderService.start();
        console.log('confirmou: ', result.value);
        this.companieService.updateUserAdmin(this.actualCompany.id, userId)
        .then(result => {
          Swal.fire({
            type: 'success',
            confirmButtonText: 'Ok',
            showCancelButton: false,
            title: 'Administrador atualizado com sucesso',
            text: `O usuário ${userName} se tornou o novo administrador da empresa ${this.actualCompany.name}`
          })
        }).catch((error) => {
          Swal.fire({
            type: 'error',
            confirmButtonText: 'Ok',
            showCancelButton: false,
            title: 'Ocorreu um erro ao tentar atualizar o administrador',
            text: `${error}`
          })
        }).finally(() => {
          this.ngxLoaderService.stop();
          this.setCompanie();
        })
      }
    })
  }

  ngOnDestroy() {
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
  }

}
